import { Table } from '@heroui/react'

import type { PosException } from '../types'
import { branchLabel, chipStyle, exceptionStatusStyles, money } from './pos-format'
import { StatusChip } from './status-chip'

interface ExceptionsTableProps {
  exceptions: PosException[]
  onOpen: (exception: PosException) => void
}

/**
 * The sales the offline queue could not land.
 *
 * A to-do list, ordered open-first by the API. The amount column shows BOTH
 * figures when a duplicate disagrees, because the pair is the finding — a
 * single number there would be the screen quietly picking a side.
 */
export function ExceptionsTable({ exceptions, onOpen }: ExceptionsTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content
          aria-label="Exceptions"
          selectionMode="single"
          selectionBehavior="replace"
          onRowAction={(key) => {
            const exception = exceptions.find((row) => row.id === String(key))

            if (exception) onOpen(exception)
          }}
        >
          <Table.Header>
            <Table.Column id="number" isRowHeader>
              Exception
            </Table.Column>
            <Table.Column id="reason">Reason</Table.Column>
            <Table.Column id="branch">Branch</Table.Column>
            <Table.Column id="amount">Amount</Table.Column>
            <Table.Column id="status">Status</Table.Column>
          </Table.Header>

          <Table.Body>
            {exceptions.map((exception) => (
              <Table.Row key={exception.id} id={exception.id}>
                <Table.Cell>
                  <span className="font-medium">{exception.number}</span>
                </Table.Cell>

                <Table.Cell>{exception.reasonTitle}</Table.Cell>

                <Table.Cell>
                  <span className="text-accent">{branchLabel(exception.branch)}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="tabular-nums">
                    {exception.counterpartAmount === null ? (
                      (money(exception.amount) ?? '—')
                    ) : (
                      // Uploaded first, then what is on file — the same order
                      // the resolution options are listed in, so the figure a
                      // manager picks lines up with the button they press.
                      <>
                        {money(exception.amount)}
                        <span className="text-muted"> / </span>
                        {money(exception.counterpartAmount)}
                      </>
                    )}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <StatusChip style={chipStyle(exceptionStatusStyles, exception.status)} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
